import { useEffect, useMemo, useState } from "react";
import {
  Button,
  DateRangePicker,
  DateRangePickerProps,
  Form,
  FormField,
  Select,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { filterQualifyingSTHs, getNewAtinToPtcMappingsForSth } from "../../sth/sth";
import { parsePtcs, Ptc } from "../../parsers/ptcParser";
import { parseSthDates } from "../../parsers/sthDatesParser";
import { isEmpty } from "lodash";
import { parseSthAtins } from "../../parsers/sthAtinParser";
import { ExcelValidationError } from "../../utils/ExcelValidationError";
import { writeAtinToPtcMappings } from "../../writers/atinToPtcWriter";
import { getWorksheetNames, registerWorksheetNamesOnChangeHandler, removeEventHandler } from "../../utils/excel";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { requiredErrorText, worksheetToOption } from "../../utils/polaris";
import {
  DATE_RANGE_LABEL,
  DEFAULT_EXCLUDED_PTCS_SHEET_NAME,
  DEFAULT_PTC_TAXABILITY_SHEET_NAME,
  DEFAULT_STH_DATES_SHEET_NAME,
  DEFAULT_TAXONOMY_EXPORT_SHEET_NAME,
  EXCLUDED_PTCS_DESCRIPTION,
  EXCLUDED_PTCS_WORKSHEET_LABEL,
  STH_DATES_CONSTRAINT_TEXT,
  STH_DATES_WORKSHEET_LABEL,
  TAXONOMY_EXPORT_CONSTRAINT_TEXT,
  TAXONOMY_EXPORT_WORKSHEET_LABEL,
  TDM_CONSTRAINT_TEXT,
  TDM_WORKSHEET_LABEL,
} from "../../utils/constants";
import { caseInsensitiveFind } from "../../utils/stringUtils";
import { parseSthExcludedPtcs } from "../../parsers/sthExcludedPtcsParser";

export default function PtcAnalysis() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  const [worksheetNames, setWorksheetNames] = useState<string[]>([]);

  const [sthDatesWorksheet, setSthDatesWorksheet] = useState<OptionDefinition>();
  const [sthDatesWorksheetError, setSthDatesWorksheetError] = useState("");

  const [taxonomyExportWorksheet, setTaxonomyExportWorksheet] = useState<OptionDefinition>();
  const [taxonomyExportWorksheetError, setTaxonomyExportWorksheetError] = useState("");

  const [ptcTdmWorksheet, setPtcTdmWorksheet] = useState<OptionDefinition>();
  const [ptcTdmWorksheetError, setPtcTdmWorksheetError] = useState("");

  const [excludedPtcsWorksheet, setExcludedPtcsWorksheet] = useState<OptionDefinition>();

  const [dateRange, setDateRange] = useState<DateRangePickerProps.AbsoluteValue>();
  const [dateRangeErrorText, setDateRangeErrorText] = useState("");

  const [qualifyingSTHsText, setQualifyingSTHsText] = useState("");

  useEffect(() => {
    getWorksheetNames()
      .then((worksheetNames) => {
        setWorksheetNames(worksheetNames);
        setDefaultWorksheetSelection(worksheetNames);
      })
      .catch((err) => setError(err.toString()));

    const worksheetNamesChangeEventHandler = registerWorksheetNamesOnChangeHandler(setWorksheetNames).catch((err) =>
      setError(err.toString())
    );

    // cleanup
    return () => {
      worksheetNamesChangeEventHandler.then((eventHandlers) => {
        if (eventHandlers) {
          eventHandlers.forEach((eventHandler) => removeEventHandler(eventHandler));
        }
      });
    };
  }, []);

  const worksheetOptions: OptionDefinition[] = useMemo(() => worksheetNames.map(worksheetToOption), [worksheetNames]);

  const setDefaultWorksheetSelection = (worksheetNames: string[]) => {
    const defaultSthDatesWorksheet = caseInsensitiveFind(worksheetNames, DEFAULT_STH_DATES_SHEET_NAME);
    if (defaultSthDatesWorksheet) setSthDatesWorksheet(worksheetToOption(defaultSthDatesWorksheet));

    const defaultTaxonomyExportWorksheet = caseInsensitiveFind(worksheetNames, DEFAULT_TAXONOMY_EXPORT_SHEET_NAME);
    if (defaultTaxonomyExportWorksheet) setTaxonomyExportWorksheet(worksheetToOption(defaultTaxonomyExportWorksheet));

    const defaultPtcTdmWorksheet = caseInsensitiveFind(worksheetNames, DEFAULT_PTC_TAXABILITY_SHEET_NAME);
    if (defaultPtcTdmWorksheet) setPtcTdmWorksheet(worksheetToOption(defaultPtcTdmWorksheet));

    const defaultExcludedPtcsWorksheet = caseInsensitiveFind(worksheetNames, DEFAULT_EXCLUDED_PTCS_SHEET_NAME);
    if (defaultExcludedPtcsWorksheet) setExcludedPtcsWorksheet(worksheetToOption(defaultExcludedPtcsWorksheet));
  };

  const getQualifyingSTHs = async (startDate: Date, endDate: Date): Promise<Map<string, string[]>> => {
    if (!sthDatesWorksheet) {
      return new Map();
    }

    const sthDates = await parseSthDates(sthDatesWorksheet.value, setLoadingText);
    if (!sthDates || isEmpty(sthDates)) {
      throw new ExcelValidationError("No STH dates data found");
    }

    const sthTypeToJurisdictions = filterQualifyingSTHs(startDate, endDate, sthDates);
    if (isEmpty(sthTypeToJurisdictions)) {
      throw new ExcelValidationError("No qualifying STHs found in inputted date range");
    }

    return sthTypeToJurisdictions;
  };

  const getQualifyingSthString = (sthTypeToJurisdiction: Map<string, string[]>) => {
    if (sthTypeToJurisdiction.size) {
      let qualifyingSthString = `Found ${sthTypeToJurisdiction.size} qualifying STH(s):\n`;
      for (let [sthType, jurisdictions] of sthTypeToJurisdiction) {
        qualifyingSthString += `${sthType}: ${jurisdictions.join(", ")}\n`;
      }
      return qualifyingSthString;
    }
    return "";
  };

  const validateFormFields = () => {
    setError("");
    let hasError = false;

    if (!sthDatesWorksheet) {
      setSthDatesWorksheetError(requiredErrorText(STH_DATES_WORKSHEET_LABEL));
      hasError = true;
    }
    if (!taxonomyExportWorksheet) {
      setTaxonomyExportWorksheetError(requiredErrorText(TAXONOMY_EXPORT_WORKSHEET_LABEL));
      hasError = true;
    }
    if (!ptcTdmWorksheet) {
      setPtcTdmWorksheetError(requiredErrorText(TDM_WORKSHEET_LABEL));
      hasError = true;
    }
    if (!dateRange) {
      setDateRangeErrorText(requiredErrorText(DATE_RANGE_LABEL));
      hasError = true;
    }

    return hasError;
  };

  const parsePtcTdmSheet = async () => {
    const ptcs = await parsePtcs(ptcTdmWorksheet.value, setLoadingText);
    if (!ptcs || isEmpty(ptcs)) {
      throw new ExcelValidationError("No PTC data found");
    }
    return ptcs;
  };

  const parseTaxonomyAndSthSheets = async (
    sthTypeToJurisdictions: Map<string, string[]>,
    ptcs: Map<string, Ptc>,
    startDate: Date
  ) => {
    const atins = await parseSthAtins(
      taxonomyExportWorksheet.value,
      sthTypeToJurisdictions,
      ptcs,
      startDate,
      setLoadingText
    );
    if (!atins || isEmpty(atins)) {
      throw new ExcelValidationError("No ATIN data found");
    }
    return atins;
  };

  const parseExcludedPtcsSheet = async (ptcs: Map<string, Ptc>) => {
    if (excludedPtcsWorksheet?.value) {
      const excludedPtcs = await parseSthExcludedPtcs(excludedPtcsWorksheet.value, ptcs, setLoadingText);
      if (!excludedPtcs || isEmpty(excludedPtcs)) {
        throw new ExcelValidationError("No excluded PTCs found");
      }
      return excludedPtcs;
    }
    return [];
  };

  const parseDataAndWriteAnalysisSheet = async () => {
    const hasError = validateFormFields();
    if (hasError) return;

    setLoading(true);

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    const sthTypeToJurisdictions = await getQualifyingSTHs(startDate, endDate);
    setQualifyingSTHsText(getQualifyingSthString(sthTypeToJurisdictions));

    const ptcs = await parsePtcTdmSheet();
    const atins = await parseTaxonomyAndSthSheets(sthTypeToJurisdictions, ptcs, startDate);
    const excludedPtcs = await parseExcludedPtcsSheet(ptcs);

    setLoadingText("Conducting PTC analysis...");
    const overallAtinToPtcMappingChanges = getNewAtinToPtcMappingsForSth(atins, ptcs, excludedPtcs);

    await writeAtinToPtcMappings(
      overallAtinToPtcMappingChanges,
      atins,
      startDate,
      endDate,
      sthTypeToJurisdictions,
      setLoadingText
    );
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        errorText={error}
        actions={
          <Button
            onClick={async () => {
              await parseDataAndWriteAnalysisSheet().catch((err) => setError(err.toString()));
              setLoading(false);
              setLoadingText("");
            }}
            loading={loading}
            variant="primary"
          >
            Analyze
          </Button>
        }
      >
        <SpaceBetween size="m" direction="vertical">
          <FormField
            label={STH_DATES_WORKSHEET_LABEL}
            errorText={sthDatesWorksheetError}
            constraintText={STH_DATES_CONSTRAINT_TEXT}
          >
            <Select
              selectedOption={sthDatesWorksheet}
              onChange={({ detail }) => {
                setSthDatesWorksheetError("");
                setSthDatesWorksheet(detail.selectedOption);
                setQualifyingSTHsText("");
              }}
              options={worksheetOptions}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField
            label={TAXONOMY_EXPORT_WORKSHEET_LABEL}
            errorText={taxonomyExportWorksheetError}
            constraintText={TAXONOMY_EXPORT_CONSTRAINT_TEXT}
          >
            <Select
              selectedOption={taxonomyExportWorksheet}
              onChange={({ detail }) => {
                setTaxonomyExportWorksheetError("");
                setTaxonomyExportWorksheet(detail.selectedOption);
              }}
              options={worksheetOptions}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField label={TDM_WORKSHEET_LABEL} errorText={ptcTdmWorksheetError} constraintText={TDM_CONSTRAINT_TEXT}>
            <Select
              selectedOption={ptcTdmWorksheet}
              onChange={({ detail }) => {
                setPtcTdmWorksheetError("");
                setPtcTdmWorksheet(detail.selectedOption);
              }}
              options={worksheetOptions}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField label={EXCLUDED_PTCS_WORKSHEET_LABEL} description={EXCLUDED_PTCS_DESCRIPTION}>
            <Select
              selectedOption={excludedPtcsWorksheet}
              onChange={({ detail }) => setExcludedPtcsWorksheet(detail.selectedOption)}
              options={worksheetOptions}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField label={DATE_RANGE_LABEL} errorText={dateRangeErrorText}>
            <DateRangePicker
              isValidRange={(range) => {
                if (range.type === "absolute") {
                  const [startDateWithoutTime] = range.startDate.split("T");
                  const [endDateWithoutTime] = range.endDate.split("T");
                  if (!startDateWithoutTime || !endDateWithoutTime) {
                    return {
                      valid: false,
                      errorMessage:
                        "The selected date range is incomplete. Select a start and end date for the date range.",
                    };
                  }
                  if (new Date(range.startDate) > new Date(range.endDate)) {
                    return {
                      valid: false,
                      errorMessage: "The selected date range is invalid. The start date must be before the end date.",
                    };
                  }
                }
                return { valid: true };
              }}
              relativeOptions={[]}
              value={dateRange}
              onChange={async ({ detail }) => {
                if (detail.value?.type === "absolute") {
                  setDateRangeErrorText("");
                  setDateRange(detail.value);

                  setQualifyingSTHsText("");
                  setError("");
                  setLoading(true);
                  const startDate = new Date(detail.value.startDate);
                  const endDate = new Date(detail.value.endDate);
                  const sthTypeToJurisdictions = await getQualifyingSTHs(startDate, endDate).catch((err) =>
                    setError(err.toString())
                  );
                  if (sthTypeToJurisdictions) {
                    setQualifyingSTHsText(getQualifyingSthString(sthTypeToJurisdictions));
                  }
                  setLoading(false);
                  setLoadingText("");
                } else {
                  setDateRangeErrorText("");
                  setDateRange(null);
                  setQualifyingSTHsText("");
                }
              }}
              rangeSelectorMode="absolute-only"
              placeholder="Filter STHs and jurisdictions by date range"
              disabled={loading}
              dateOnly
            />
          </FormField>
        </SpaceBetween>
        <p>
          <small style={{ whiteSpace: "pre-line" }}>{qualifyingSTHsText}</small>
        </p>
        <p>
          <small>{loadingText}</small>
        </p>
      </Form>
    </form>
  );
}
